import React, {
	useEffect,
	useState,
	useRef,
	useMemo,
	useCallback,
} from "react";
import classes from "./UnlinkedBuyers.module.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Button, Space } from "antd";
import { Pie } from "react-chartjs-2";
import "chart.js/auto";
import api from "../../utils/axios";
import DefaultInput from "../../components/form/DefaultInput";
import useLang from "../../hooks/useLang";
import { userRoles } from "../../types/enums";
import { getUserRoleFromToken } from "../../utils/Auth";
import { CopyOutlined } from "@ant-design/icons";
import { useMediaQuery } from "@mui/material";

interface Buyer {
	id: number;
	firstName: string;
	lastName: string;
	latitude: number;
	longitude: number;
	address: string;
	sectionId: number;
	phone: string;
	email: string | null;
	sellerId: number;
	temporaryBuyerDetailsId: number;
}

const Buyer = () => {
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [isClient, setIsClient] = useState<boolean>();
	const [buyer, setBuyer] = useState<Buyer[]>([]);
	const [filteredName, setFilteredName] = useState<string>("");
	const [filteredPhone, setFilteredPhone] = useState<string>("");
	const [uuid, setUuid] = useState<string>("");
	const [showLink, setShowLink] = useState<boolean>(false);
	const { strings } = useLang("unlinkedBuyers");
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);

	let grid = useRef(null);
	const nameAndSurnameGetter = (params: any) => {
		const text = params.data.firstName + " " + params.data.lastName;
		return text;
	};

	const getBuyers = async () => {
		try {
			const response = await api.get("Buyer/unlinked-buyer");
			if (response.status === 200) {
				const buyerData = response.data;
				setBuyer(buyerData);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	// Data loads once on open; the column effect below only relabels on a language switch.
	useEffect(() => {
		getBuyers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		userRoleId === userRoles.CLIENT
			? setIsClient(true)
			: setIsClient(false);
		setTableColumns([
			{
				width: 105,
				headerName: strings?.headers.progress,
				cellRenderer: (params: any) => {
					const progressValue = 60;
					return <Pie data={data} />;
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.headers.code,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 80,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "firstName",
				valueGetter: nameAndSurnameGetter,
				headerName: strings?.headers.firstLastName,
				width: 150,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "latitude",
				headerName: strings?.headers.latitude,
				width: 110,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "longitude",
				headerName: strings?.headers.longitude,
				width: 110,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "address",
				headerName: strings?.headers.address,
				width: 210,
				suppressSizeToFit: isSmallScreen,
			},

			{
				field: "phone",
				headerName: strings?.headers.phone,
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				width: 100,
				cellRenderer: (params: any) => {
					const handleEditClick = (id: number) => {
						const registerOrderURL = "/register-order";
						const urlWithId = `${registerOrderURL}?id=${id}`;
						window.open(urlWithId, "_blank");
					};
					return (
						<div className={classes.buttonsAgGrid}>
							<Button
								onClick={() => handleEditClick(params.data.id)}
								className={classes.tableButtonFirst}
							>
								{strings?.buttons?.open}
							</Button>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [
		setTableColumns,
		strings?.buttons?.open,
		strings?.headers.address,
		strings?.headers.code,
		strings?.headers.firstLastName,
		strings?.headers.latitude,
		strings?.headers.longitude,
		strings?.headers.phone,
		strings?.headers.progress,
		strings?.headers.sectionId,
	]);

	const data = {
		spacing: 5,
		datasets: [
			{
				data: [45, 55],
				backgroundColor: ["#F2C94C", "#ffff"],
				borderColor: "#F2C94C",
			},
		],
	};

	const createTemporaryLink = async () => {
		try {
			const response = await api.post("Buyer/create-temp-link");
			if (response) {
				setShowLink(true);
				setUuid(response?.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const linkRef = useRef<any>(null);

	const copyToClipboard = () => {
		if (linkRef) {
			linkRef.current.select();
			document.execCommand("copy");
			if (navigator.clipboard) {
				navigator.clipboard.writeText(linkRef.current.value);
			}
		}
	};

	const filteredData = useMemo(() => {
		return buyer.filter((item: any) => {
			return (
				(!filteredName ||
					item.firstName
						.toLowerCase()
						.includes(filteredName.toLowerCase())) &&
				(!filteredPhone ||
					item.phone
						.toLowerCase()
						.includes(filteredPhone.toLowerCase()))
			);
		});
	}, [buyer, filteredName, filteredPhone]);

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	return (
		<div className={classes.mainContainer}>
			{/* <FinanceStatistic /> */}
			<div className={classes.heroContainer}>
				<p className="settlementTitle">{strings?.headers.newBuyers}</p>
				<div className="flex flex-row justify-between items-center">
					<div className={classes.inputs}>
						<DefaultInput
							label={strings?.headers.name}
							value={filteredName}
							onChange={(e: any) =>
								setFilteredName(e.target.value)
							}
							className={classes.input}
							placeholder={strings?.headers.name}
						/>
						<DefaultInput
							label={strings?.headers.phoneNr}
							value={filteredPhone}
							onChange={(e: any) =>
								setFilteredPhone(e.target.value)
							}
							className={classes.input}
							placeholder={strings?.headers.phoneNr}
						/>
					</div>

					{isClient && (
						<>
							<div className="flex flex-col gap-2">
								<div>
									<Button onClick={createTemporaryLink}>
										{strings?.createTempLink}
									</Button>
								</div>
								{showLink ? (
									<Space direction="horizontal">
										<input
											type="text"
											readOnly
											value={`${window.location.origin}/temporary-register-link/${uuid}`}
											style={{
												width: 300,
												height: 35,
												padding: 10,
												borderRadius: 5,
												outline: "none",
											}}
											ref={linkRef}
										/>
										<Button
											onClick={copyToClipboard}
											className="copy-button"
										>
											{strings?.copy} <CopyOutlined />
										</Button>
									</Space>
								) : (
									<></>
								)}
							</div>
						</>
					)}
				</div>
				<div
					className="ag-theme-alpine"
					style={{
						height: 600,
						width: "100%",
					}}
				>
					<AgGridReact
						rowData={filteredData}
						columnDefs={tableColumns}
						defaultColDef={defaultColDef}
						undoRedoCellEditing={true}
						undoRedoCellEditingLimit={20}
						suppressCopyRowsToClipboard={true}
						defaultExcelExportParams={defaultExcelExportSettings}
						rowSelection="multiple"
						ref={grid}
						enableRangeSelection={true}
						gridOptions={{ rowHeight: 60 }}
						onFirstDataRendered={onFirstDataRendered}
					/>
				</div>
			</div>
		</div>
	);
};

export default Buyer;
